import { useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import type { Key } from 'react-aria-components';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';
import { Select } from '@/lib/base/forms/select/select';
import { SelectItem } from '@/lib/base/forms/select/select-item';
import { SlideoutMenu } from '@/lib/base/overlays/slideout-menu';
import type { WebhookSecretCreatedData } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { WebhookSecretCreatedModal } from '@/lib/crm/components/webhooks/WebhookSecretCreatedModal';
import { logger } from '@/lib/logger';

import {
  createInboundWebhook,
  mintWebhookSecret,
  SECRET_AUTH_METHODS,
  WEBHOOK_AUTH_METHOD_ITEMS,
  WEBHOOK_SECRET_INSTRUCTIONS,
} from './inbound-webhook-service';

const CREATE_HEADER = (
  <>
    <h2 className='text-heading-3 text-[var(--color-text-primary)]'>
      New inbound webhook
    </h2>
    <p className='text-sm text-[var(--color-text-tertiary)] mt-1'>
      Name the webhook and choose how incoming requests are authenticated.
    </p>
  </>
);

export interface WebhookCreateSlideoutProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: number;
  onCreated: () => void;
}

interface PendingSecret {
  entityId: string;
  authMethod: string;
}

interface WebhookCreateFormProps {
  onSubmit: (params: { displayName: string; authMethod: string }) => void;
  onRetry: () => void;
  pendingSecret: PendingSecret | null;
  isSubmitting: boolean;
  userId: number | null;
  onDirtyChange?: (dirty: boolean) => void;
}

function noop() {}

/**
 * The create form body. Extracted so it mounts only while the slideout is
 * open (the parent renders it behind `isOpen`). The mint mutation and
 * one-time-secret state live in the always-mounted parent, NOT here: if the
 * slideout closes mid-flight this form unmounts, and a mutation owned here
 * would drop its onSuccess — losing the one-time secret display.
 */
function WebhookCreateForm({
  onSubmit,
  onRetry,
  pendingSecret,
  isSubmitting,
  userId,
  onDirtyChange,
}: WebhookCreateFormProps) {
  const [name, setName] = useState('');
  const [authMethod, setAuthMethod] = useState('bearer');

  const isDirty = name.trim() !== '' || authMethod !== 'bearer';
  useEffect(() => {
    onDirtyChange?.(isDirty);
    return () => onDirtyChange?.(false);
  }, [isDirty, onDirtyChange]);

  const handleAuthMethodChange = useCallback((key: Key | null) => {
    if (key !== null) {
      setAuthMethod(String(key));
    }
  }, []);

  const handleSubmit = useCallback(() => {
    onSubmit({ displayName: name.trim(), authMethod });
  }, [onSubmit, name, authMethod]);

  return (
    <div className='flex flex-col gap-4 pb-6'>
      <Input
        label='Name'
        value={name}
        onChange={setName}
        isRequired
        size='md'
      />
      <Select
        label='Authentication'
        items={WEBHOOK_AUTH_METHOD_ITEMS}
        value={authMethod}
        onChange={handleAuthMethodChange}
      >
        {item => (
          <SelectItem id={item.id} description={item.description}>
            {item.label}
          </SelectItem>
        )}
      </Select>
      {pendingSecret ? (
        <div className='flex flex-col gap-2'>
          <p className='text-sm text-[var(--color-text-error-primary)]'>
            The webhook was created, but generating its secret failed. Retry to
            try again.
          </p>
          <Button color='primary' onClick={onRetry} isLoading={isSubmitting}>
            Retry
          </Button>
        </div>
      ) : (
        <Button
          color='primary'
          onClick={handleSubmit}
          isDisabled={!name.trim() || isSubmitting || userId === null}
          isLoading={isSubmitting}
        >
          Create webhook
        </Button>
      )}
    </div>
  );
}

export function WebhookCreateSlideout({
  isOpen,
  onOpenChange,
  orgId,
  onCreated,
}: WebhookCreateSlideoutProps) {
  const { user } = useAuthInfo();
  const userId: number | null = user?.id ?? null;

  const [secretData, setSecretData] = useState<WebhookSecretCreatedData | null>(
    null
  );
  const [instruction, setInstruction] = useState<string | undefined>(undefined);
  const [pendingSecret, setPendingSecret] = useState<PendingSecret | null>(
    null
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // The mint mutation is owned here, in the always-mounted wrapper, rather
  // than in the form body. If the user closes the slideout while a mint is
  // in flight the form unmounts; a mutation owned there would drop its
  // onSuccess/onError, losing the one-time secret display and the retry
  // state.
  const mintMutation = useMutation({
    mutationFn: ({ entityId, authMethod }: PendingSecret) =>
      mintWebhookSecret(orgId, userId as number, entityId, authMethod),
    onSuccess: (data, vars) => {
      setSecretData(prev => prev ?? data);
      setInstruction(
        WEBHOOK_SECRET_INSTRUCTIONS[
          vars.authMethod as keyof typeof WEBHOOK_SECRET_INSTRUCTIONS
        ]
      );
      setPendingSecret(null);
      onOpenChange(false);
    },
    onError: (err, vars) => {
      logger.error(
        'Failed to mint webhook secret',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error('Could not create the webhook secret. Retry to try again.');
      setPendingSecret({
        entityId: vars.entityId,
        authMethod: vars.authMethod,
      });
    },
  });

  const createMutation = useMutation({
    mutationFn: (p: { displayName: string; authMethod: string }) =>
      createInboundWebhook({
        orgId,
        userId: userId as number,
        displayName: p.displayName,
        authMethod: p.authMethod,
      }),
    onSuccess: (entityId, p) => {
      onCreated();
      if (SECRET_AUTH_METHODS.has(p.authMethod)) {
        mintMutation.mutate({ entityId, authMethod: p.authMethod });
      } else {
        onOpenChange(false);
        toast.success('Webhook created');
      }
    },
    onError: err => {
      logger.error(
        'Failed to create webhook',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'crm_events', org_id: orgId }
      );
      toast.error('Could not create the webhook. Please try again.');
    },
  });

  const handleSubmit = useCallback(
    (p: { displayName: string; authMethod: string }) => {
      if (userId === null) {
        toast.error('Could not create the webhook. Please try again.');
        return;
      }
      if (!createMutation.isPending) {
        createMutation.mutate(p);
      }
    },
    [createMutation, userId]
  );

  // Retry re-calls only the secrets endpoint — the webhook already exists,
  // so re-creating it here would produce a duplicate live webhook.
  const handleRetry = useCallback(() => {
    if (pendingSecret && !mintMutation.isPending) {
      logger.warn('Retrying webhook secret mint', {
        component: 'crm_events',
        org_id: orgId,
      });
      mintMutation.mutate(pendingSecret);
    }
  }, [pendingSecret, mintMutation, orgId]);

  const handleCloseSecretModal = useCallback(() => {
    setSecretData(null);
  }, []);

  return (
    <>
      <SlideoutMenu
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        header={CREATE_HEADER}
        hasUnsavedChanges={hasUnsavedChanges}
        onDiscardUnsavedChanges={noop}
      >
        {isOpen && (
          <WebhookCreateForm
            onSubmit={handleSubmit}
            onRetry={handleRetry}
            pendingSecret={pendingSecret}
            isSubmitting={createMutation.isPending || mintMutation.isPending}
            userId={userId}
            onDirtyChange={setHasUnsavedChanges}
          />
        )}
      </SlideoutMenu>
      <WebhookSecretCreatedModal
        isOpen={!!secretData}
        onClose={handleCloseSecretModal}
        data={secretData}
        instruction={instruction}
      />
    </>
  );
}
